/** Client-side profile picture rules (JPG/PNG). */
export const PROFILE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const PROFILE_IMAGE_SIZE_LIMIT_LABEL = '10 MB';

/** Re-encode above this so uploads stay under typical gateway limits (~512 KB–1 MB). */
const PROFILE_UPLOAD_PREPARE_BYTES = 200 * 1024;
const PROFILE_UPLOAD_TARGET_BYTES = 400 * 1024;
const PROFILE_IMAGE_MAX_DIMENSION = 800;

export const PROFILE_IMAGE_FILE_SIZE_ERROR = `Profile image must be ${PROFILE_IMAGE_SIZE_LIMIT_LABEL} or smaller.`;
export const PROFILE_IMAGE_FILE_TYPE_ERROR = 'Please choose a JPG or PNG image.';
export const PROFILE_IMAGE_TOO_LARGE_SERVER_ERROR =
  'The image is too large to upload. Please choose a smaller JPG or PNG file.';
export const PROFILE_IMAGE_UPLOAD_UNAVAILABLE_ERROR =
  'Unable to upload the image. Please try a smaller JPG or PNG file (under 1 MB) and try again.';

export function isAllowedProfileImageFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png');
}

export function validateProfileImageFile(file: File): { ok: true } | { ok: false; message: string } {
  if (!isAllowedProfileImageFile(file)) {
    return { ok: false, message: PROFILE_IMAGE_FILE_TYPE_ERROR };
  }
  if (file.size > PROFILE_IMAGE_MAX_BYTES) {
    return { ok: false, message: PROFILE_IMAGE_FILE_SIZE_ERROR };
  }
  return { ok: true };
}

function isGenericTransportErrorMessage(msg: string): boolean {
  return /^(network error\.?|request timed out\.?|unable to reach the server)/i.test(msg);
}

/** Maps HTTP status and apiClient transport errors to user-facing upload messages. */
export function profileUploadErrorMessage(status: number, fallback?: string): string {
  if (status === 413) return PROFILE_IMAGE_TOO_LARGE_SERVER_ERROR;
  if (status === 0) return PROFILE_IMAGE_UPLOAD_UNAVAILABLE_ERROR;
  const msg = fallback?.trim();
  if (msg && !isGenericTransportErrorMessage(msg)) return msg;
  if (status >= 500) return 'Image upload failed due to a server error. Please try again.';
  if (status === 408 || status === 429) return PROFILE_IMAGE_TOO_LARGE_SERVER_ERROR;
  return PROFILE_IMAGE_UPLOAD_UNAVAILABLE_ERROR;
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

/** Downscale / re-encode photos so uploads stay under typical reverse-proxy limits. */
export async function prepareProfileImageFile(file: File): Promise<File> {
  if (file.size <= PROFILE_UPLOAD_PREPARE_BYTES) return file;

  let img: HTMLImageElement;
  try {
    img = await loadImageFromFile(file);
  } catch {
    return file;
  }

  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;
  if (!width || !height) return file;

  const scaleToFit = () => {
    if (width <= PROFILE_IMAGE_MAX_DIMENSION && height <= PROFILE_IMAGE_MAX_DIMENSION) return;
    const ratio = Math.min(PROFILE_IMAGE_MAX_DIMENSION / width, PROFILE_IMAGE_MAX_DIMENSION / height);
    width = Math.max(1, Math.round(width * ratio));
    height = Math.max(1, Math.round(height * ratio));
  };

  scaleToFit();

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'profile';
  const mime = 'image/jpeg';
  const ext = 'jpg';

  const renderBlob = async (w: number, h: number, quality: number): Promise<Blob | null> => {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvasToBlob(canvas, mime, quality);
  };

  let quality = 0.82;
  let lastBlob: Blob | null = null;
  for (let attempt = 0; attempt < 10; attempt++) {
    const blob = await renderBlob(width, height, quality);
    if (!blob) break;
    lastBlob = blob;
    if (blob.size <= PROFILE_UPLOAD_TARGET_BYTES) {
      return new File([blob], `${baseName}.${ext}`, { type: mime });
    }
    quality -= 0.08;
    if (quality >= 0.4) continue;
    scaleToFit();
    width = Math.max(1, Math.round(width * 0.85));
    height = Math.max(1, Math.round(height * 0.85));
    quality = 0.75;
  }

  if (lastBlob) {
    return new File([lastBlob], `${baseName}.${ext}`, { type: mime });
  }
  return file;
}
